import { isUserRole } from '@aslgo/shared';
import type { UserRole } from '@aslgo/shared';

export const MAX_VISIBLE_PER_SPEAKER = 5;

export interface IncomingSegment {
  id: string;
  text: string;
  final: boolean;
  language?: string;
}

export interface LocalCaption {
  id: string;
  speakerIdentity: string;
  speakerRole: UserRole;
  text: string;
  final: boolean;
  updatedAt: number;
}

export interface CaptionState {
  buffers: Record<UserRole, Map<string, LocalCaption>>;
  speaking: Record<UserRole, boolean>;
  micOff: Record<UserRole, boolean>;
}

export function emptyState(): CaptionState {
  return {
    buffers: { learner: new Map(), guide: new Map() },
    speaking: { learner: false, guide: false },
    micOff: { learner: false, guide: false },
  };
}

interface SpeakerInfo {
  identity: string;
  role: string | undefined;
}

export function applySegments(
  state: CaptionState,
  segments: IncomingSegment[],
  speaker: SpeakerInfo,
  now: number,
): CaptionState {
  if (!isUserRole(speaker.role)) return state;
  const role: UserRole = speaker.role;

  const buf = new Map(state.buffers[role]);
  for (const seg of segments) {
    buf.set(seg.id, {
      id: seg.id,
      speakerIdentity: speaker.identity,
      speakerRole: role,
      text: seg.text,
      final: seg.final,
      updatedAt: now,
    });
  }
  if (buf.size > MAX_VISIBLE_PER_SPEAKER) {
    const sorted = [...buf.values()].sort((a, b) => a.updatedAt - b.updatedAt);
    const keep = sorted.slice(-MAX_VISIBLE_PER_SPEAKER);
    buf.clear();
    for (const c of keep) buf.set(c.id, c);
  }
  return { ...state, buffers: { ...state.buffers, [role]: buf } };
}

export function applyMicState(
  state: CaptionState,
  role: UserRole,
  isMicEnabled: boolean,
): CaptionState {
  return {
    ...state,
    micOff: { ...state.micOff, [role]: !isMicEnabled },
  };
}

export function applySpeakingState(
  state: CaptionState,
  role: UserRole,
  isSpeaking: boolean,
): CaptionState {
  return {
    ...state,
    speaking: { ...state.speaking, [role]: isSpeaking },
  };
}

/** Convenience: turn a row's Map into a stable-ordered array (oldest → newest). */
export function rowToArray(buf: Map<string, LocalCaption>): LocalCaption[] {
  return [...buf.values()].sort((a, b) => a.updatedAt - b.updatedAt);
}

